// Step 2: the workflow from the marketplace, best rated first (best match first when searching),
// a page at a time, with what each one needs. The group pins the latest published version. (A
// type with one workflow has it chosen already: TypeStep picks it.)
import { FlowArrow } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { SearchBox } from "../../../catalog/SearchBox";
import { CatalogIcon } from "../../../catalog/icons";
import { useT } from "../../../i18n";
import { RatingSummary } from "../../../ratings/Stars";
import { EmptyState, ErrorState, Loading } from "../../../ui/states";
import { Pill } from "../../../ui/status";
import { requirementLabel } from "../../../workflows/requirements/model";
import { marketplaceWorkflowsQuery } from "../api";
import { type Draft, type MarketplaceWorkflow, requirementsOf } from "../draft";
import { ChoiceCard } from "../frame/ChoiceCard";
import { Pager } from "../frame/Pager";
import { useDebounced } from "../jira/useJira";

function Needs({ workflow }: { workflow: MarketplaceWorkflow }) {
  const t = useT().tasks.create.workflow;
  const needs = requirementsOf(workflow);
  return (
    <span className="mt-3 flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-medium text-gray-500">{needs.length === 0 ? t.needsNothing : t.needs}</span>
      {needs.map((r, i) => (
        <Pill key={i} tone={r.kind === "isolation" ? "blue" : "gray"} className="whitespace-normal">
          {requirementLabel(r)}
        </Pill>
      ))}
    </span>
  );
}

function WorkflowCard({ workflow: w, draft, onChange }: { workflow: MarketplaceWorkflow; draft: Draft; onChange: (patch: Partial<Draft>) => void }) {
  const t = useT().tasks.create.workflow;
  const { usedIn } = useT().ratings;
  return (
    <ChoiceCard
      name="workflow"
      value={w.id}
      checked={draft.workflow?.id === w.id}
      onSelect={() => draft.workflow?.id !== w.id && onChange({ workflow: w, bindings: {} })}
      icon={<CatalogIcon name={w.icon} fallback={FlowArrow} />}
      title={w.name}
    >
      {w.description ? <span className="line-clamp-2">{w.description}</span> : null}
      <span className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <RatingSummary stats={w.stats} />
        <span>{usedIn(w.stats.usage_count)}</span>
        <span>{t.version(w.version.version, w.version.prompt_steps + w.version.approval_steps)}</span>
      </span>
      <Needs workflow={w} />
    </ChoiceCard>
  );
}

export function WorkflowStep({ draft, onChange }: { draft: Draft; onChange: (patch: Partial<Draft>) => void }) {
  const t = useT().tasks.create.workflow;
  const [text, setText] = useState("");
  const q = useDebounced(text.trim());
  // The page belongs to one search: a new search starts again at the first page.
  const [paging, setPaging] = useState({ q, page: 0 });
  const page = paging.q === q ? paging.page : 0;
  const workflows = useQuery({ ...marketplaceWorkflowsQuery(draft.taskTypeId, q, page), placeholderData: (previous) => previous });
  const search = <SearchBox label={t.search} placeholder={t.searchPlaceholder} text={text} onChange={setText} />;
  if (workflows.isPending) return <Loading />;
  if (workflows.isError) return <ErrorState title={t.loadFailed} message={workflows.error.message} />;
  const { items, hasNext } = workflows.data;
  if (items.length === 0 && q === "" && page === 0) {
    return (
      <EmptyState compact icon={<FlowArrow weight="duotone" />} title={t.none}>
        {t.noneBody}
      </EmptyState>
    );
  }
  const chosenElsewhere = draft.workflow && !items.some((w) => w.id === draft.workflow?.id) ? draft.workflow : null;
  return (
    <div className="grid gap-4">
      {search}
      {chosenElsewhere ? (
        <div className="grid gap-2">
          <p className="text-xs font-medium text-gray-500">{t.chosen}</p>
          <WorkflowCard workflow={chosenElsewhere} draft={draft} onChange={onChange} />
        </div>
      ) : null}
      {items.length === 0 ? (
        <EmptyState compact icon={<FlowArrow weight="duotone" />} title={t.noMatch(q)}>
          {t.noMatchBody}
        </EmptyState>
      ) : (
        <div className="grid gap-4" role="radiogroup" aria-label={t.label} aria-busy={workflows.isPlaceholderData}>
          {items.map((w) => (
            <WorkflowCard key={w.id} workflow={w} draft={draft} onChange={onChange} />
          ))}
        </div>
      )}
      <Pager page={page} hasNext={hasNext} onPage={(next) => setPaging({ q, page: next })} />
    </div>
  );
}
